import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { API, testApi, getTestToken } from "@/lib/testApi";
import { toast } from "sonner";

/**
 * Pricing + product selection. Serves both pathways.
 * Route:  /pricing/investor  |  /pricing/company
 *
 * Server-authoritative flow: POST /api/checkout/session with only
 * {pack_id, pathway}. Server derives every other value.
 */
export default function Pricing({ pathway }) {
  const [params] = useSearchParams();
  const [catalog, setCatalog] = useState([]);
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const [selecting, setSelecting] = useState(null);
  const navigate = useNavigate();
  const isCompany = pathway === "company";

  useEffect(() => {
    document.title = `Pricing · ${isCompany ? "Companies" : "Investors"} · ImpactVest`;
    testApi.get("/checkout/catalog").then((r) => {
      setCatalog(r.data.catalog || []);
      setPaymentsEnabled(!!r.data.payments_enabled);
    }).catch(() => setCatalog([]));
  }, [isCompany]);

  async function handleChoose(pack) {
    if (!paymentsEnabled) {
      toast.error("ImpactVest payments are not yet enabled for public purchase.");
      return;
    }
    if (!getTestToken()) {
      navigate(`/access?next=/pricing/${pathway}`);
      return;
    }
    setSelecting(pack.pack_id);
    try {
      const r = await testApi.post("/checkout/session", {
        pack_id: pack.pack_id, pathway,
      });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(d?.message || d?.code || "Unable to start checkout.");
      setSelecting(null);
    }
  }

  return (
    <div className="min-h-screen bg-iv-bg text-iv-fg" data-testid={`pricing-${pathway}-page`}>
      <header className="border-b border-iv-line bg-iv-surface">
        <div className="iv-container flex items-center justify-between h-[68px]">
          <Link to="/" className="iv-body-sm" style={{ color: "var(--iv-fg-2)", textDecoration: "none" }}>
            ← Back to ImpactVest
          </Link>
          <span className="iv-caption">{isCompany ? "Companies" : "Investors"} · Pricing</span>
        </div>
      </header>

      <main className="iv-container py-14 md:py-20 max-w-[1080px] mx-auto">
        <div className="iv-caption mb-4">{isCompany ? "For companies" : "For investors"}</div>
        <h1 className="iv-h1 mb-6" data-testid="pricing-h1">
          {isCompany
            ? "Publish an evidence pack investors can trust."
            : "Build a decision passport your committee can defend."}
        </h1>
        <p className="iv-body-lg mb-4">
          One credit purchases one complete analysis:{" "}
          {isCompany ? "an Investor Evidence Pack" : "a Decision Passport"}. Credits do not expire.
          Payments processed securely by Stripe. Billing support: office@impactvest.com.
        </p>
        {!paymentsEnabled && (
          <div className="border border-iv-line-strong bg-iv-surface p-4 mb-8 iv-body-sm"
               data-testid="pricing-payments-disabled-banner">
            Public purchase is not yet enabled. The pricing catalogue below is authoritative;
            the buy buttons open once activation completes. Contact office@impactvest.com to be
            notified.
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {catalog.map((pack) => {
            const unit = Math.round((pack.amount_cents / pack.credits) / 100);
            return (
              <div
                key={pack.pack_id}
                data-testid={`pack-card-${pack.pack_id}`}
                className="border border-iv-line-strong bg-white p-7 flex flex-col"
              >
                <div className="iv-caption mb-2">{pack.pack_id.replace("pack-", "")} credit{pack.credits > 1 ? "s" : ""}</div>
                <div className="font-serif-display mb-2" style={{ fontSize: 40, lineHeight: 1.02 }}>
                  {pack.amount_display}
                </div>
                <div className="iv-body-sm mb-4" style={{ color: "var(--iv-fg-2)" }}>
                  {pack.credits} × {isCompany ? "Investor Evidence Pack" : "Decision Passport"}
                </div>
                <ul className="iv-body-sm mb-6 space-y-2" style={{ color: "var(--iv-fg-2)" }}>
                  <li className="flex gap-2"><CheckCircle2 size={16} className="text-iv-green shrink-0 mt-1" /> ${unit} per analysis</li>
                  <li className="flex gap-2"><CheckCircle2 size={16} className="text-iv-green shrink-0 mt-1" /> Private live workspace, created automatically</li>
                  <li className="flex gap-2"><CheckCircle2 size={16} className="text-iv-green shrink-0 mt-1" /> Cascade-delete + audit at any time</li>
                </ul>
                <button
                  data-testid={`buy-${pack.pack_id}`}
                  onClick={() => handleChoose(pack)}
                  disabled={!paymentsEnabled || selecting === pack.pack_id}
                  className="iv-btn iv-btn--primary mt-auto"
                >
                  {selecting === pack.pack_id ? (
                    <><Loader2 size={16} className="animate-spin" /> Redirecting…</>
                  ) : (
                    <>Start analysis <ArrowRight size={14} /></>
                  )}
                </button>
              </div>
            );
          })}
        </div>

        <div className="iv-caption max-w-[68ch]">
          Credits are consumed only when you press &ldquo;Release&rdquo; on a Decision Passport or an
          Investor Evidence Pack. Read the{" "}
          <a href="/legal/retention" className="hover:underline" style={{ color: "var(--iv-green)" }}>data retention notice</a>
          {" "}before you upload.
        </div>
      </main>
    </div>
  );
}
